import React, { useMemo } from 'react';
import { Network } from 'lucide-react';
import ChartCard from './ChartCard';
import { Slider } from '../../ui/slider';
import { SERIES_COLORS } from './chartTheme';
import { MODE_COLORS } from './HevCharts';
import { STRATEGY_KEYS, STRATEGY_LABELS } from '../../../lib/hevEnergyTools';

const h = React.createElement;
const MECH = SERIES_COLORS[1];
const ELEC = SERIES_COLORS[0];
const HEAT = SERIES_COLORS[7];
// Below this |power| a path is drawn as idle.
const ACTIVE_KW = 0.3;

// P2 layout: mechanical chain engine -> clutch -> motor-generator -> gearbox -> final drive ->
// wheels, electrical branch motor-generator <-> inverter <-> HV battery, friction brakes at the
// wheels.
const NODES = {
  engine: { x: 10, y: 20, w: 88, label: 'Engine' },
  clutch: { x: 132, y: 20, w: 58, label: 'Clutch' },
  mg: { x: 224, y: 20, w: 92, label: 'Motor-Gen' },
  inverter: { x: 350, y: 20, w: 100, label: 'Inverter' },
  gearbox: { x: 224, y: 140, w: 92, label: 'Gearbox' },
  fd: { x: 112, y: 140, w: 78, label: 'Final drive' },
  battery: { x: 350, y: 140, w: 100, label: 'HV battery' },
  wheels: { x: 112, y: 250, w: 78, label: 'Wheels' },
  brakes: { x: 8, y: 250, w: 70, label: 'Friction brakes' },
};
const NODE_H = 46;
// Edges: [from, to, kind, x1, y1, x2, y2] in the "forward" direction (drive / discharge).
const EDGES = [
  ['engine', 'clutch', 'mech', 98, 43, 132, 43],
  ['clutch', 'mg', 'mech', 190, 43, 224, 43],
  ['mg', 'gearbox', 'mech', 270, 66, 270, 140],
  ['gearbox', 'fd', 'mech', 224, 163, 190, 163],
  ['fd', 'wheels', 'mech', 151, 186, 151, 250],
  ['wheels', 'brakes', 'heat', 112, 273, 78, 273],
  ['inverter', 'mg', 'elec', 350, 43, 316, 43],
  ['battery', 'inverter', 'elec', 400, 140, 400, 66],
];

function edgeFlows(row) {
  // kW on each edge in the forward direction (negative = flowing backwards).
  const eng = row.engine_kw;
  const mot = row.motor_kw;
  const shaft = row.mode === 'charge' && Math.abs(eng + mot) < 0.5 ? 0 : eng + mot;
  return {
    'engine-clutch': eng,
    'clutch-mg': eng,
    'mg-gearbox': shaft,
    'gearbox-fd': shaft,
    'fd-wheels': shaft,
    'wheels-brakes': -row.friction_kw,
    'inverter-mg': mot,
    'battery-inverter': row.battery_kw,
  };
}

function Arrow({ id, color }) {
  return h('marker', { id, viewBox: '0 0 10 10', refX: 9, refY: 5, markerWidth: 6, markerHeight: 6, orient: 'auto-start-reverse' },
    h('path', { d: 'M 0 0 L 10 5 L 0 10 z', style: { fill: color } }));
}

export default function HevArchitectureDiagram({ series, index, onIndex, strategy, onStrategy, hybrid, modeLabels }) {
  const n = series.time_s.length;
  const i = Math.min(Math.max(index ?? 0, 0), n - 1);
  const st = series.strategies[strategy];
  const row = useMemo(() => ({
    engine_kw: st.engine_kw[i], motor_kw: st.motor_kw[i], battery_kw: st.battery_kw[i],
    friction_kw: st.friction_kw[i], mode: st.mode[i], gear: st.gear[i], soc: st.soc_pct[i],
  }), [st, i]);
  const flows = edgeFlows(row);
  const activeNodes = new Set();
  for (const [a, b, kind] of EDGES) {
    const p = flows[`${a}-${b}`];
    if (Math.abs(p) > ACTIVE_KW && (hybrid || kind !== 'elec')) { activeNodes.add(a); activeNodes.add(b); }
  }
  const clutchClosed = row.engine_kw > ACTIVE_KW || row.mode === 'brake' && strategy === 'ice';
  const strategies = hybrid ? STRATEGY_KEYS : ['ice'];

  const svgChildren = [
    h('defs', { key: 'defs' }, h(Arrow, { id: 'hev-arrow-mech', color: MECH }), h(Arrow, { id: 'hev-arrow-elec', color: ELEC }),
      h(Arrow, { id: 'hev-arrow-heat', color: HEAT })),
  ];
  for (const [a, b, kind, x1, y1, x2, y2] of EDGES) {
    if (!hybrid && kind === 'elec') continue;
    const p = flows[`${a}-${b}`];
    const active = Math.abs(p) > ACTIVE_KW;
    const color = kind === 'elec' ? ELEC : kind === 'heat' ? HEAT : MECH;
    const [sx, sy, ex, ey] = p >= 0 ? [x1, y1, x2, y2] : [x2, y2, x1, y1];
    svgChildren.push(h('line', {
      key: `e-${a}-${b}`, x1: sx, y1: sy, x2: ex, y2: ey,
      style: {
        stroke: active ? color : 'hsl(var(--border))', strokeWidth: active ? 3.5 : 2,
        strokeDasharray: kind === 'elec' ? '6 4' : kind === 'heat' ? '2 3' : undefined,
      },
      markerEnd: active ? `url(#hev-arrow-${kind})` : undefined,
      'data-testid': `hev-edge-${a}-${b}`, 'data-active': active ? 'true' : 'false',
    }));
    if (active) {
      const vertical = x1 === x2;
      svgChildren.push(h('text', {
        key: `l-${a}-${b}`, x: vertical ? x1 + 6 : (x1 + x2) / 2, y: vertical ? (y1 + y2) / 2 + 4 : y1 - 8,
        textAnchor: vertical ? 'start' : 'middle', style: { fill: 'hsl(var(--foreground))', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' },
      }, `${Math.abs(p).toFixed(1)} kW`));
    }
  }
  for (const [key, node] of Object.entries(NODES)) {
    if (!hybrid && ['mg', 'inverter', 'battery'].includes(key)) continue;
    const active = activeNodes.has(key);
    svgChildren.push(h('g', { key: `n-${key}`, 'data-testid': `hev-node-${key}` },
      h('rect', {
        x: node.x, y: node.y, width: node.w, height: NODE_H, rx: 5,
        style: { fill: active ? 'hsl(var(--secondary))' : 'hsl(var(--card))', stroke: active ? 'hsl(var(--primary))' : 'hsl(var(--border))', strokeWidth: active ? 2 : 1.25 },
      }),
      h('text', {
        x: node.x + node.w / 2, y: node.y + (key === 'clutch' || key === 'gearbox' || key === 'battery' ? 21 : 28), textAnchor: 'middle',
        style: { fill: 'hsl(var(--foreground))', fontSize: key === 'brakes' ? 10.5 : 12, fontWeight: 600, fontFamily: 'Chivo, sans-serif' },
      }, node.label),
      key === 'clutch' ? h('text', { x: node.x + node.w / 2, y: node.y + 37, textAnchor: 'middle', style: { fill: 'hsl(var(--muted-foreground))', fontSize: 10, fontFamily: 'JetBrains Mono, monospace' } }, clutchClosed ? 'closed' : 'open') : null,
      key === 'gearbox' ? h('text', { x: node.x + node.w / 2, y: node.y + 37, textAnchor: 'middle', style: { fill: 'hsl(var(--muted-foreground))', fontSize: 10, fontFamily: 'JetBrains Mono, monospace' } }, row.gear ? `gear ${row.gear}` : 'neutral') : null,
      key === 'battery' && row.soc != null ? h('text', { x: node.x + node.w / 2, y: node.y + 37, textAnchor: 'middle', style: { fill: 'hsl(var(--muted-foreground))', fontSize: 10, fontFamily: 'JetBrains Mono, monospace' } }, `SoC ${row.soc.toFixed(1)}%`) : null,
    ));
  }

  return (
    <ChartCard icon={Network} title="Architecture & Power Flow" testId="hev-architecture"
      description="Parallel P2 layout. Solid orange = mechanical power, dashed blue = electrical, dotted red = heat into the friction brakes; arrows show the direction power flows at the selected instant. Hover any time trace or use the slider.">
      <div className="flex flex-wrap items-center gap-2 mb-3" role="group" aria-label="Strategy shown in the diagram">
        {strategies.map((k) => (
          <button key={k} type="button" onClick={() => onStrategy(k)} data-testid={`hev-diagram-strategy-${k}`}
            className={`px-2.5 py-1 rounded-md border font-mono text-[11px] uppercase tracking-wider ${k === strategy ? 'border-primary bg-secondary text-foreground' : 'border-border text-muted-foreground'}`}>
            {STRATEGY_LABELS[k]}
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_220px] gap-4 items-start">
        {/* Built with createElement: a JSX {array} child inside <svg> gets wrapped in an HTML <span>
            by the dev-only visual-edits Babel plugin, which is invalid in SVG and renders nothing. */}
        {h('svg', {
          viewBox: '0 0 460 306', className: 'w-full h-auto max-w-[620px]', role: 'img',
          'aria-label': 'P2 hybrid architecture power-flow diagram', 'data-testid': 'hev-architecture-svg',
        }, ...svgChildren)}
        <div className="space-y-2 font-mono text-xs" data-testid="hev-diagram-readout">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: MODE_COLORS[row.mode] }} />
            <span className="font-semibold text-sm" data-testid="hev-diagram-mode">{modeLabels[row.mode] || row.mode}</span>
          </div>
          <p className="text-muted-foreground">t = {series.time_s[i].toFixed(0)} s · {series.speed_kph[i].toFixed(1)} km/h</p>
          <p>Engine {row.engine_kw.toFixed(1)} kW</p>
          {hybrid && <p>Motor {row.motor_kw.toFixed(1)} kW · Battery {row.battery_kw.toFixed(1)} kW</p>}
          <p>Friction brakes {Math.abs(row.friction_kw).toFixed(1)} kW</p>
        </div>
      </div>
      <div className="mt-4">
        <label htmlFor="hev-scrubber" className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Time</label>
        <Slider id="hev-scrubber" min={0} max={n - 1} step={1} value={[i]} onValueChange={(v) => onIndex(v[0])} className="mt-2" data-testid="hev-scrubber" aria-label="Time scrubber" />
      </div>
    </ChartCard>
  );
}
